/**
 * Meeting URL / token / join-window helpers.
 *
 * Server-side encrypts (meetingId, date, time, user, astrologer) into
 * an opaque base64url token and embeds it in `meeting.meetingURL` like
 * `https://apnasanatan.com/meeting/<token>`. The token is what the
 * in-call screens need as their route param — the server's
 * validateMeetingToken endpoint decrypts it and confirms the meeting
 * is in the join window.
 *
 * In-call billing endpoints, on the other hand, key on the raw
 * `meeting._id` because that's the BillingSession.sessionId. The
 * hook flips from "token" (URL param) to "_id" (channel/billing key)
 * after validateMeetingToken returns the populated meeting.
 *
 * tl;dr:
 *   - Route params (Consult Now button)        → encrypted token
 *   - Billing / channel name (server-side)     → meeting._id
 *   - Details page link (info display)         → meeting._id
 */

/**
 * Extract the meeting token from a `meeting.meetingURL` like
 *   "https://app.apnasanatan.com/meeting/<token>"
 * or relative variants. Returns `""` if the URL is missing or
 * malformed so callers can short-circuit ("no link, no join").
 *
 * Mirrors mobile-app's tokenFromMeetingURL (Consultation/
 * AppointmentCard.jsx) — keep them in sync if you touch it.
 */
export const tokenFromMeetingURL = (meetingURL) => {
  if (!meetingURL || typeof meetingURL !== "string") return "";
  try {
    const u = new URL(meetingURL);
    const parts = u.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  } catch {
    const idx = meetingURL.lastIndexOf("/");
    if (idx === -1) return meetingURL || "";
    return meetingURL.slice(idx + 1) || "";
  }
};

/**
 * Resolve the partner-app in-call route segment ("voice" | "video")
 * from a Meeting.mode. Meeting model uses "call" for voice; partner-
 * app's route segment is "voice". Anything else (including "chat" and
 * the legacy default "Video/Voice" string) falls back to video.
 */
export const inCallSegmentForMode = (mode) =>
  mode === "call" ? "voice" : "video";

/* ─── Join-window logic ──────────────────────────────────────────
 *
 * Source of truth is the server's validateMeetingToken
 * (server/src/controllers/meeting/meeting.controller.js):
 *   - Reject if meeting date < today AND not the same calendar day
 *   - Accept if same calendar day AND time-until-meeting ≤ 10min
 *   - Reject otherwise (too early; too late on a different day)
 *
 * We surface a tighter UI bound here than "same day" because allowing
 * a join 8 hours after the scheduled start is misleading — the user is
 * almost certainly not still waiting. JOIN_WINDOW_AFTER_MS caps the
 * post-start visibility of the Consult Now button at 60 minutes; the
 * server still gates the actual token mint, so the UI bound is just
 * about presentation, not correctness.
 *
 * Mobile (Consultation/AppointmentCard.jsx) uses the same 10/60
 * policy. Two separate files (web utility here, mobile inline helper
 * there) by necessity — mobile is a React Native package, web is
 * a Vite/React package, no shared module path. Keep them aligned
 * when changing the window bounds.
 */

const JOIN_WINDOW_BEFORE_MS = 10 * 60_000;
const JOIN_WINDOW_AFTER_MS = 60 * 60_000;

/**
 * Compute join-window state for a meeting. Returns `{canJoin, hint}`.
 *
 * `meetingDate` can be a Date or anything `new Date(...)` accepts.
 * `status` is the Meeting.status enum value; only "pending" meetings
 * can ever be in-window.
 *
 *   canJoin: true  → render an enabled Consult Now / Join link
 *   canJoin: false → disable the link and surface `hint` in a tooltip
 *
 * The hint is null when canJoin=true (nothing to say), or a short
 * human-readable explanation otherwise ("Starts in 2h", "Window closed").
 */
export const joinWindowFor = (meetingDate, status) => {
  if (status !== "pending") {
    return { canJoin: false, hint: null };
  }
  if (!meetingDate) {
    return { canJoin: false, hint: null };
  }

  const start = new Date(meetingDate).getTime();
  if (Number.isNaN(start)) {
    return { canJoin: false, hint: null };
  }

  const now = Date.now();
  const earliest = start - JOIN_WINDOW_BEFORE_MS;
  const latest = start + JOIN_WINDOW_AFTER_MS;

  if (now < earliest) {
    const diffMin = Math.ceil((start - now) / 60_000);
    const hint =
      diffMin >= 60
        ? `Starts in ${Math.floor(diffMin / 60)}h ${diffMin % 60}m`
        : `Starts in ${diffMin}m`;
    return { canJoin: false, hint };
  }

  if (now > latest) {
    return { canJoin: false, hint: "Join window closed" };
  }

  return { canJoin: true, hint: null };
};
